import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getDevelopers } from '../api/developersApi'
import { createComplex, updateComplex } from '../api/liveComplexesApi'

/**
 * Complex Modal Component
 * Form for creating/editing residential complexes
 */
export function ComplexModal({ isOpen, onClose, complex }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!complex

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      city: '',
      developer: '',
    },
  })

  // Fetch developers for select
  const { data: developersData } = useQuery({
    queryKey: ['developers'],
    queryFn: () => getDevelopers(),
  })

  // Populate form when editing
  useEffect(() => {
    if (complex) {
      reset({
        name: complex.name || '',
        city: complex.city?.toString() || '',
        developer: complex.developer?.toString() || '',
      })
    } else {
      reset({
        name: '',
        city: '',
        developer: '',
      })
    }
  }, [complex, reset])

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      const data = {
        name: formData.name,
        ...(formData.city && { city: parseInt(formData.city, 10) }),
        ...(formData.developer && { developer: parseInt(formData.developer, 10) }),
      }

      if (isEdit) {
        return updateComplex(complex.id, data)
      } else {
        return createComplex(data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complexes'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit
          ? t('complexes.complexUpdated')
          : t('complexes.complexCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('complexes.complexSaveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  const developerOptions = developersData?.data?.map((dev) => ({
    value: dev.id.toString(),
    label: dev.name,
  })) || []

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('complexes.editComplex') : t('complexes.createComplex')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Legend fontSize="md" fontWeight="600">
              {t('complexes.complexInfo')}
            </Fieldset.Legend>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('complexes.name')} *`}
                      placeholder={t('complexes.namePlaceholder')}
                      error={errors.name?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      label={t('complexes.city')}
                      placeholder={t('complexes.cityPlaceholder')}
                      error={errors.city?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="developer"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('complexes.developer')}
                      placeholder={t('complexes.selectDeveloper')}
                      options={developerOptions}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              isLoading={saveMutation.isPending}
            >
              {isEdit ? t('common.save') : t('common.create')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}

export default ComplexModal
